import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Spinner, Alert, Table } from "react-bootstrap";
import toastStore from "../../store/ToastStore";
import { getChannelCommissions, saveChannelCommission, removeChannelCommission } from "../../api/channelCommissionsApi";
import { getOrderOrigins } from "../../api/reportsApi";
import { formatYmd } from "../../utils/dateRange";

function extractErrorMessage(e, fallback) {
  return e?.response?.data?.message || e?.message || fallback;
}

export default function ChannelCommissionsModal({ server, show, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // {channelName: {percent: string, saving: bool, dirty: bool}}
  const [rows, setRows] = useState({});
  const [newChannelName, setNewChannelName] = useState("");

  const load = useCallback(() => {
    if (!server?.id) return;
    setLoading(true);
    setError(null);

    const today = new Date();
    const monthAgo = new Date(today);
    monthAgo.setDate(today.getDate() - 30);

    Promise.all([
      getChannelCommissions(server.id),
      getOrderOrigins(server.id, formatYmd(monthAgo), formatYmd(today)).catch(() => ({ origins: [] })),
    ])
      .then(([commissionsRes, originsRes]) => {
        const next = {};
        for (const o of originsRes?.origins || []) {
          next[o.name] = { percent: "0", saving: false, dirty: false };
        }
        for (const c of commissionsRes?.commissions || []) {
          next[c.channel_name] = { percent: String(c.commission_percent), saving: false, dirty: false };
        }
        setRows(next);
      })
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [server?.id]);

  useEffect(() => {
    if (show) {
      load();
      setNewChannelName("");
    }
  }, [show, load]);

  const setPercent = (name, percent) => {
    setRows((prev) => ({ ...prev, [name]: { ...prev[name], percent, dirty: true } }));
  };

  const handleSave = async (name) => {
    const row = rows[name];
    if (!row) return;
    const value = Number(row.percent);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      toastStore.error("Процент должен быть от 0 до 100.");
      return;
    }
    setRows((prev) => ({ ...prev, [name]: { ...prev[name], saving: true } }));
    try {
      await saveChannelCommission(server.id, name, value);
      setRows((prev) => ({ ...prev, [name]: { ...prev[name], saving: false, dirty: false } }));
      toastStore.success(`Комиссия для «${name}» сохранена.`);
    } catch (e) {
      setRows((prev) => ({ ...prev, [name]: { ...prev[name], saving: false } }));
      toastStore.error(extractErrorMessage(e, "Не удалось сохранить комиссию."));
    }
  };

  const handleRemove = async (name) => {
    try {
      await removeChannelCommission(server.id, name);
      setRows((prev) => {
        const next = { ...prev };
        next[name] = { percent: "0", saving: false, dirty: false };
        return next;
      });
      toastStore.success(`Комиссия для «${name}» сброшена.`);
    } catch (e) {
      toastStore.error(extractErrorMessage(e, "Не удалось сбросить комиссию."));
    }
  };

  const handleAddChannel = () => {
    const name = newChannelName.trim();
    if (!name) return;
    if (rows[name]) {
      toastStore.error("Такой источник уже есть в списке.");
      return;
    }
    setRows((prev) => ({ ...prev, [name]: { percent: "0", saving: false, dirty: true } }));
    setNewChannelName("");
  };

  const names = Object.keys(rows).sort((a, b) => a.localeCompare(b, "ru"));

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Комиссии по источнику заказа — {server?.name || server?.server}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex align-items-center gap-2 text-body-secondary py-3">
            <Spinner animation="border" size="sm" />
            Загрузка…
          </div>
        ) : (
          <>
            {error && <Alert variant="danger">Не удалось загрузить список источников.</Alert>}
            <Form.Text className="d-block mb-3 text-body-secondary">
              Укажите % комиссии для каждого источника заказа (конкретного агрегатора/платформы, например
              yandex_food или delivery_club) — он вычитается из валовой выручки на странице «Каналы продаж» →
              «Источник заказа», чтобы показывать реальную чистую выручку. Комиссия привязана именно к источнику,
              а не к типу заказа (зал/доставка/самовывоз), потому что один тип заказа может объединять несколько
              разных агрегаторов с разной комиссией. iiko не знает о комиссиях сторонних сервисов — это ручная
              настройка. Список ниже собран из заказов за последние 30 дней; можно также добавить вручную.
            </Form.Text>

            {names.length === 0 ? (
              <div className="text-body-secondary small">
                За последние 30 дней не найдено ни одного источника — проверьте позже или добавьте вручную.
              </div>
            ) : (
              <Table size="sm" bordered>
                <thead>
                  <tr>
                    <th>Источник</th>
                    <th style={{ width: 140 }}>Комиссия, %</th>
                    <th style={{ width: 160 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {names.map((name) => {
                    const row = rows[name];
                    return (
                      <tr key={name}>
                        <td className="align-middle">{name}</td>
                        <td>
                          <Form.Control
                            type="number"
                            size="sm"
                            min={0}
                            max={100}
                            step="0.1"
                            value={row.percent}
                            onChange={(e) => setPercent(name, e.target.value)}
                          />
                        </td>
                        <td className="d-flex gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={!row.dirty || row.saving}
                            onClick={() => handleSave(name)}
                          >
                            {row.saving ? <Spinner animation="border" size="sm" /> : "Сохранить"}
                          </Button>
                          <Button variant="outline-secondary" size="sm" onClick={() => handleRemove(name)}>
                            Сброс
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}

            <Form.Group className="d-flex gap-2 mt-3">
              <Form.Control
                size="sm"
                placeholder="Название источника вручную (как в iiko)"
                value={newChannelName}
                onChange={(e) => setNewChannelName(e.target.value)}
              />
              <Button variant="outline-primary" size="sm" onClick={handleAddChannel}>
                Добавить
              </Button>
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Закрыть
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
